import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Thermometer,
  Flame,
  ShowerHead,
  Bath,
  Shirt,
  UtensilsCrossed,
  Smartphone,
  BellRing,
  BadgeCheck,
  ArrowLeft,
} from "lucide-react";
import Logo from "@/components/landing/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HOUSE_IMG =
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const LIVING_IMG =
  "https://images.unsplash.com/photo-1513694203232-719a280e022f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const BATH_IMG =
  "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const INTERIOR_IMG =
  "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const inputClass =
  "w-full px-4 py-3 rounded-lg border border-slate-300 focus:outline-none focus:border-emerald-500 text-sm bg-white";
const labelClass = "block text-xs font-bold text-slate-700 uppercase mb-1";

const consumption = [
  { label: "Oppvarming", pct: 60, display: "60 %" },
  { label: "Varmtvann", pct: 18, display: "15–20 %" },
  { label: "Elektriske apparater", pct: 15, display: "15 %" },
];

const heatTips = [
  {
    icon: Thermometer,
    title: "Senk romtemperaturen",
    text: "For hver grad du senker innetemperaturen, kan du spare rundt fem prosent av strømmen som går til oppvarming. 20–22 grader på dagtid er en god tommelfingerregel – senk gjerne temperaturen om natten og i rom som ikke er i bruk.",
  },
  {
    icon: Flame,
    title: "Installer varmepumpe",
    text: "En varmepumpe gir jevn temperatur og bedre inneklima året rundt – varme på kalde vinterdager og kjøling når sommeren står på. I vinterhalvåret, når strømmen er dyrest, kan varmepumpen redusere strømutgiftene dine merkbart.",
  },
];

const waterTips = [
  {
    icon: ShowerHead,
    title: "Dusj smartere",
    text: "Kort ned dusjtiden, og spre gjerne dusjinga utover dagen hvis dere er flere i husstanden. Du trenger ikke vente til strømmen er billigst – litt planlegging og en noe lavere vanntemperatur gjør en merkbar forskjell over tid.",
  },
  {
    icon: Bath,
    title: "Dropp badekaret i hverdagen",
    text: "Et badekar rommer mye varmtvann, og all oppvarmingen av det trekker strøm. Velg dusjen til daglig, og la badekaret være helgekos.",
  },
  {
    icon: Shirt,
    title: "Fyll vaskemaskinen – la tørketrommelen stå",
    text: "Kjør vaskemaskinen full hver gang, og ta klesvasken på dagtid av sikkerhetshensyn. Tørketrommelen er en av de største strømtyvene i huset – heng heller klærne til tørk. Må du bruke den, la døren stå åpen etterpå så overskuddsvarmen varmer opp rommet.",
  },
  {
    icon: UtensilsCrossed,
    title: "Bruk oppvaskmaskinen",
    text: "Mot vanlig tro lønner det seg faktisk å bruke oppvaskmaskin fremfor å vaske opp for hånd – fordi håndoppvask bruker mye strøm til å varme opp vannet. Fyll maskinen og sett den på med god samvittighet.",
  },
];

const initialForm = { kontaktperson: "", epost: "", telefon: "", melding: "" };

const PrivatPage = () => {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    document.title = "Rstrøm Privat – 0 påslag og 49 kr i måneden | Rstrøm AS";
  }, []);

  const update = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/henvendelser`, { ...form, kundetype: "privat" });
      toast.success("Takk for interessen!", {
        description: "Vi tar kontakt så snart Rstrøm Privat er klart for deg.",
      });
      setForm(initialForm);
    } catch (err) {
      toast.error("Noe gikk galt", {
        description: "Interessen kunne ikke registreres. Prøv igjen, eller ring oss på 921 74 274.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A]" data-testid="privat-page">
      {/* Toppmeny */}
      <header className="sticky top-0 z-50 bg-[#0B132B]/95 backdrop-blur-md border-b border-white/10 text-white">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <a href="/" aria-label="Til forsiden" data-testid="privat-logo-link">
            <Logo />
          </a>
          <div className="flex items-center gap-4">
            <a
              href="/"
              className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-slate-300 hover:text-emerald-400 transition-colors"
              data-testid="privat-back-link"
            >
              <ArrowLeft className="w-4 h-4" /> Til forsiden
            </a>
            <a
              href="#privat-skjema"
              className="px-5 py-2.5 rounded-md bg-emerald-500 hover:bg-emerald-400 text-[#0B132B] font-semibold text-sm transition-colors shadow-sm"
              data-testid="privat-header-cta"
            >
              Meld interesse
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden bg-[#0B132B] text-white" data-testid="privat-hero">
        <div className="absolute inset-0">
          <img src={HOUSE_IMG} alt="Moderne norsk enebolig i kveldslys" className="w-full h-full object-cover opacity-55" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B132B] via-[#0B132B]/65 to-[#0B132B]/40" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-12 items-center w-full">
          <div className="animate-entry">
            <span className="inline-block px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 text-xs font-bold uppercase tracking-widest mb-6">
              Nyhet – nå også for privatkunder
            </span>
            <h1 className="font-serif-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-6">
              Strøm hjem til deg. Enkelt og ærlig.
            </h1>
            <p className="text-lg text-slate-200 leading-relaxed mb-8 max-w-xl">
              Med Rstrøm Privat betaler du det strømmen faktisk koster – ikke et
              øre mer. Ingen skjulte kostnader. Ingen tilleggstjenester. Bare en
              lav, fast månedsavgift og full oversikt.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="#privat-skjema"
                className="px-8 py-4 rounded-md bg-emerald-500 hover:bg-emerald-400 text-[#0B132B] font-bold text-base transition-colors shadow-lg text-center"
                data-testid="privat-hero-cta"
              >
                Meld din interesse
              </a>
              <a
                href="#norgespris"
                className="px-8 py-4 rounded-md bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold text-base transition-colors text-center"
                data-testid="privat-norgespris-link"
              >
                Alt om Norgespris 2027
              </a>
            </div>

            <div className="mt-12 pt-8 border-t border-white/10 grid grid-cols-3 gap-4 text-left max-w-xl">
              <div className="border-l-2 border-emerald-400/60 pl-4">
                <div className="text-xl font-bold text-white tracking-tight" data-testid="privat-stat-markup">0 øre</div>
                <div className="text-xs text-slate-400 mt-1">Påslag per kWh</div>
              </div>
              <div className="border-l-2 border-emerald-400/60 pl-4">
                <div className="text-xl font-bold text-white tracking-tight" data-testid="privat-stat-monthly">49 kr</div>
                <div className="text-xs text-slate-400 mt-1">Fast månedsavgift</div>
              </div>
              <div className="border-l-2 border-emerald-400/60 pl-4">
                <div className="text-xl font-bold text-emerald-400 tracking-tight" data-testid="privat-stat-binding">Ingen</div>
                <div className="text-xs text-slate-400 mt-1">Bindingstid</div>
              </div>
            </div>
          </div>

          {/* Avtalekort */}
          <div className="p-8 rounded-2xl bg-white text-[#0F172A] shadow-2xl border-t-4 border-t-emerald-500" data-testid="privat-plan-card">
            <span className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">
              Privatavtale
            </span>
            <h2 className="text-3xl font-bold text-[#0B132B] mt-1">Rstrøm Privat</h2>
            <p className="text-sm text-slate-600 mt-2">
              Spotpris helt uten påslag – for deg som vil ha det enkelt.
            </p>

            <div className="my-6 py-6 border-y border-slate-100 space-y-4">
              <div className="flex justify-between items-baseline">
                <span className="text-sm text-slate-600">Påslag per kWh:</span>
                <span className="text-2xl font-bold text-emerald-600" data-testid="privat-plan-markup">0 øre</span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-sm text-slate-600">Fast månedsavgift:</span>
                <span className="text-2xl font-bold text-[#0B132B]" data-testid="privat-plan-monthly">49 kr</span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-sm text-slate-600">Norgespris:</span>
                <span className="text-sm font-semibold text-emerald-600">Kompatibel</span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-sm text-slate-600">Bindingstid:</span>
                <span className="text-sm font-semibold text-emerald-600" data-testid="privat-plan-binding">Ingen</span>
              </div>
            </div>

            <ul className="space-y-3 text-sm text-slate-600 mb-8">
              {[
                "Ingen skjulte kostnader",
                "Ingen tilleggstjenester",
                "Ingen bindingstid – si opp når du vil",
                "Ingen oppsigelsesgebyr",
                "Fungerer sammen med Norgespris",
              ].map((t) => (
                <li key={t} className="flex items-center gap-2">
                  <BadgeCheck className="w-4 h-4 text-emerald-500 shrink-0" />
                  {t}
                </li>
              ))}
            </ul>

            <a
              href="#privat-skjema"
              className="block w-full py-3.5 px-4 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-[#0B132B] font-bold text-center text-sm transition-colors"
              data-testid="privat-plan-cta"
            >
              Jeg vil vite mer
            </a>

            <div className="mt-4 flex justify-center gap-5 text-xs">
              <a
                href="/spotprisavtalevilkar-rstrom-privat.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-500 hover:text-emerald-600 underline underline-offset-2 transition-colors"
                data-testid="privat-terms-link"
              >
                Avtalevilkår (PDF)
              </a>
              <a
                href="/angrerettskjema-rstrom.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-500 hover:text-emerald-600 underline underline-offset-2 transition-colors"
                data-testid="privat-withdrawal-link"
              >
                Angreskjema (PDF)
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Om avtalen */}
      <section className="py-20 bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-serif-heading text-3xl sm:text-4xl font-bold text-[#0B132B] mb-6">
            Ærlig strøm, uten fine print
          </h2>
          <p className="text-slate-600 leading-relaxed">
            Vi har tatt prinsippet fra bedriftsmarkedet hjem til norske
            husholdninger: strøm skal være enkelt å forstå. Hos oss betaler du
            innkjøpsprisen for strømmen – uten et eneste øre i påslag – pluss en
            lav, fast månedsavgift på 49 kroner. Det er hele modellen. Ingen
            skjulte gebyrer, ingen tilleggstjenester du ikke har bedt om, ingen
            bindingstid og ingen oppsigelsesgebyr – du står fritt til å gå
            videre når du vil. Avtalen støtter dessuten Norgespris fullt ut,
            for deg som vil ha ekstra forutsigbarhet.
          </p>
        </div>
      </section>

      {/* Norgespris 2027 */}
      <section id="norgespris" className="py-24 bg-slate-100" data-testid="norgespris-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-14">
            <span className="text-xs font-bold uppercase tracking-widest text-emerald-600">
              Godt å vite
            </span>
            <h2 className="font-serif-heading text-3xl sm:text-4xl font-bold text-[#0B132B] mt-2">
              Alt om Norgespris 2027
            </h2>
            <p className="text-slate-600 mt-4 leading-relaxed">
              Regjeringen foreslår å heve Norgespris fra 50 øre i 2026 til{" "}
              <strong>56,25 øre per kWh inkludert mva.</strong> (45 øre eks.
              mva.) i 2027. Den endelige prisen vedtas i statsbudsjettet i
              desember. Her får du en enkel oversikt over hva som skjer.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: BellRing,
                title: "Bestilling åpner senere",
                text: "Det blir mulig å bestille Norgespris for 2027 – men ikke helt ennå. Første periode med Norgespris varer ut 2026.",
              },
              {
                icon: BadgeCheck,
                title: "Ny pris vedtas i desember",
                text: "Forslaget er 56,25 øre per kWh inkludert mva. og 45 øre uten mva. Prisen fastsettes endelig i statsbudsjettet i desember.",
              },
              {
                icon: Smartphone,
                title: "Vi gir deg beskjed",
                text: "Som Rstrøm-kunde får du melding fra oss så snart Norgespris for 2027 kan bestilles – du trenger ikke følge med selv.",
              },
            ].map((c) => (
              <div
                key={c.title}
                className="p-8 rounded-xl bg-white border border-slate-200 hover:border-emerald-500/40 transition-colors"
                data-testid={`norgespris-card-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="w-12 h-12 rounded-lg bg-emerald-100 border border-emerald-200 flex items-center justify-center text-emerald-700 mb-5">
                  <c.icon className="w-6 h-6" aria-hidden="true" />
                </div>
                <h3 className="text-lg font-bold text-[#0B132B] mb-2">{c.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* App-teaser */}
      <section className="py-24 bg-white border-b border-slate-200" data-testid="app-section">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative order-2 lg:order-1">
            <div className="w-full aspect-[4/3] rounded-2xl overflow-hidden border border-slate-200 relative">
              <img src={INTERIOR_IMG} alt="Moderne og lyst hjemmeinteriør" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B132B]/60 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 px-3 py-1.5 rounded bg-[#0B132B]/80 border border-white/10 text-emerald-300 text-xs font-bold uppercase tracking-wider">
                Under etablering
              </div>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <span className="text-xs font-bold uppercase tracking-widest text-emerald-600">
              Kommer snart
            </span>
            <h2 className="font-serif-heading text-3xl sm:text-4xl font-bold text-[#0B132B] mt-2 mb-6">
              Full kontroll i appen
            </h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              Appen vår er under etablering og lanseres snart. Med den får du
              full oversikt over strømforbruket ditt, strømprisen time for time
              og regningene dine – rett i lomma. Enkelt, slik strøm skal være.
            </p>
            <ul className="space-y-3 text-sm text-slate-600">
              {[
                "Se forbruket ditt i sanntid",
                "Følg strømprisen time for time",
                "Full oversikt over månedsavgift og kostnader",
              ].map((t) => (
                <li key={t} className="flex items-center gap-2">
                  <Smartphone className="w-4 h-4 text-emerald-600 shrink-0" />
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Strømforbruk */}
      <section className="py-24 bg-slate-900 text-white" data-testid="consumption-section">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="text-xs font-bold uppercase tracking-widest text-emerald-400">
              Strømforbruk
            </span>
            <h2 className="font-serif-heading text-3xl sm:text-4xl font-bold mt-2">
              Hva bruker vi strøm til?
            </h2>
            <p className="text-slate-400 mt-4 text-sm leading-relaxed max-w-xl mx-auto">
              Slik fordeler strømforbruket seg i en typisk husholdning. Har du
              én eller to elbiler som lades hjemme, kan fordelingen se litt
              annerledes ut.
            </p>
          </div>

          <div className="space-y-8">
            {consumption.map((c) => (
              <div key={c.label} data-testid={`consumption-bar-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="flex justify-between text-sm mb-2">
                  <span className="font-semibold">{c.label}</span>
                  <span className="text-emerald-400 font-bold">{c.display}</span>
                </div>
                <div className="h-4 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full rounded-full bg-emerald-500" style={{ width: `${c.pct}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sparetips */}
      <section className="py-24 bg-slate-50" data-testid="tips-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-xs font-bold uppercase tracking-widest text-emerald-600">
              Spar strøm hjemme
            </span>
            <h2 className="font-serif-heading text-3xl sm:text-4xl font-bold text-[#0B132B] mt-2">
              Enkle grep som senker regningen
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 mb-12">
            <div className="rounded-2xl overflow-hidden border border-slate-200 relative min-h-[280px]">
              <img src={LIVING_IMG} alt="Lunt og lyst stueinteriør" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
              <div className="absolute bottom-0 p-6">
                <h3 className="text-2xl font-bold text-white">Tips for oppvarming</h3>
                <p className="text-sm text-slate-200 mt-1">Oppvarming er den største strømposten – små justeringer gir store utslag.</p>
              </div>
            </div>
            <div className="space-y-6">
              {heatTips.map((t) => (
                <div key={t.title} className="p-6 rounded-xl bg-white border border-slate-200 flex gap-5" data-testid={`tip-${t.title.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="w-12 h-12 rounded-lg bg-[#0B132B] flex items-center justify-center text-emerald-400 shrink-0">
                    <t.icon className="w-6 h-6" aria-hidden="true" />
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-[#0B132B] mb-1.5">{t.title}</h4>
                    <p className="text-sm text-slate-600 leading-relaxed">{t.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <div className="space-y-6 order-2 lg:order-1">
              {waterTips.map((t) => (
                <div key={t.title} className="p-6 rounded-xl bg-white border border-slate-200 flex gap-5" data-testid={`tip-${t.title.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="w-12 h-12 rounded-lg bg-[#0B132B] flex items-center justify-center text-emerald-400 shrink-0">
                    <t.icon className="w-6 h-6" aria-hidden="true" />
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-[#0B132B] mb-1.5">{t.title}</h4>
                    <p className="text-sm text-slate-600 leading-relaxed">{t.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="rounded-2xl overflow-hidden border border-slate-200 relative min-h-[280px] order-1 lg:order-2">
              <img src={BATH_IMG} alt="Moderne baderom med dusj" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
              <div className="absolute bottom-0 p-6">
                <h3 className="text-2xl font-bold text-white">Tips for varmtvann</h3>
                <p className="text-sm text-slate-200 mt-1">Varmtvann står for 15–20 % av forbruket – her er det mye å hente.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interesseskjema */}
      <section id="privat-skjema" className="py-24 bg-[#0B132B] text-white" data-testid="privat-form-section">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-10">
            <span className="text-xs font-bold uppercase tracking-widest text-emerald-400">
              Rstrøm Privat
            </span>
            <h2 className="font-serif-heading text-3xl sm:text-4xl font-bold mt-2 mb-4">
              Meld din interesse
            </h2>
            <p className="text-slate-300 text-sm leading-relaxed max-w-xl mx-auto">
              Vil du ha strøm uten påslag og med 49 kr i fast månedsavgift?
              Legg igjen navn og kontaktinfo, så tar vi kontakt så snart
              avtalen er klar for deg.
            </p>
          </div>

          <div className="bg-white text-[#0F172A] p-8 sm:p-10 rounded-2xl shadow-2xl">
            <form className="space-y-4" onSubmit={handleSubmit} data-testid="privat-form">
              <div>
                <label htmlFor="p_navn" className={labelClass}>Navn</label>
                <input
                  id="p_navn" type="text" required placeholder="Navn Navnesen"
                  className={inputClass} value={form.kontaktperson} onChange={update("kontaktperson")}
                  data-testid="privat-form-name-input"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="p_epost" className={labelClass}>E-postadresse</label>
                  <input
                    id="p_epost" type="email" required placeholder="navn@epost.no"
                    className={inputClass} value={form.epost} onChange={update("epost")}
                    data-testid="privat-form-email-input"
                  />
                </div>
                <div>
                  <label htmlFor="p_telefon" className={labelClass}>Telefonnummer</label>
                  <input
                    id="p_telefon" type="tel" required placeholder="Ditt mobilnummer"
                    className={inputClass} value={form.telefon} onChange={update("telefon")}
                    data-testid="privat-form-phone-input"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="p_melding" className={labelClass}>Melding (valgfritt)</label>
                <textarea
                  id="p_melding" rows="3" placeholder="F.eks. spørsmål om Norgespris eller avtalen..."
                  className={inputClass} value={form.melding} onChange={update("melding")}
                  data-testid="privat-form-message-input"
                />
              </div>
              <button
                type="submit" disabled={sending}
                className="w-full py-4 rounded-lg bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 disabled:cursor-not-allowed text-[#0B132B] font-bold text-base transition-colors shadow-md"
                data-testid="privat-form-submit-button"
              >
                {sending ? "Sender..." : "Meld interesse – helt uforpliktende"}
              </button>
              <p className="text-[11px] text-slate-500 text-center">
                Vi behandler opplysningene dine konfidensielt. Ingen binding ved innsending.
              </p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivatPage;
